import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Plug, Check, X } from "lucide-react";

const statusStyle = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-accent/15 text-accent",
  paid: "bg-primary/10 text-primary",
  overdue: "bg-destructive/10 text-destructive",
};

const today = () => new Date().toISOString().slice(0, 10);
const plus30 = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); };

const empty = () => ({
  customer_id: "", customer_name: "", customer_email: "",
  items: [{ description: "", quantity: 1, unit_price: 0 }],
  tax_rate: 0, status: "draft", issue_date: today(), due_date: plus30(), notes: "",
});

export default function Invoices() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [waveConnected, setWaveConnected] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(null);

  const load = () => api.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    if (isAdmin) api.get("/wave/settings").then((r) => setWaveConnected(r.data.connected && r.data.business_id)).catch(() => {});
  }, [isAdmin]);

  const openNew = () => { setForm(empty()); setEditing(null); setOpen(true); };
  const openEdit = (inv) => {
    setForm({ ...empty(), ...inv, items: inv.items.length ? inv.items : empty().items });
    setEditing(inv.id); setOpen(true);
  };

  const onCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({ ...f, customer_id: id, customer_name: c ? c.name : "", customer_email: c ? c.email : "" }));
  };

  const setItem = (i, k, v) => {
    const next = form.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it));
    setForm({ ...form, items: next });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((a, b) => a + (Number(b.quantity) || 0) * (Number(b.unit_price) || 0), 0);
  const tax = subtotal * (Number(form.tax_rate) || 0) / 100;
  const total = subtotal + tax;

  const payloadOf = (f) => ({
    customer_id: f.customer_id, customer_name: f.customer_name, customer_email: f.customer_email,
    items: f.items.map((it) => ({ description: it.description, quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0 })),
    tax_rate: Number(f.tax_rate) || 0, status: f.status, issue_date: f.issue_date, due_date: f.due_date, notes: f.notes,
  });

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Select a customer");
    try {
      if (editing) await api.put(`/invoices/${editing}`, payloadOf(form));
      else await api.post("/invoices", payloadOf(form));
      toast.success(editing ? "Invoice updated" : "Invoice created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/invoices/${id}`); toast.success("Invoice deleted"); load(); };

  const syncWave = async (inv) => {
    setSyncing(inv.id);
    try {
      await api.post(`/wave/sync-invoice/${inv.id}`);
      toast.success("Synced to Wave accounting");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSyncing(null); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Create invoices, track payment status, and sync to Wave.</p>
        <Button data-testid="add-invoice-btn" onClick={openNew} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">
          <Plus className="h-4 w-4 mr-1.5" /> New Invoice
        </Button>
      </div>

      <Card className="border-border rounded-lg overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Invoice #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No invoices yet.</TableCell></TableRow>
            ) : (
              items.map((inv) => (
                <TableRow key={inv.id} data-testid="invoice-row" className="hover:bg-muted/30 transition-colors duration-200">
                  <TableCell className="font-semibold">{inv.invoice_number}</TableCell>
                  <TableCell>{inv.customer_name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{inv.issue_date}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{inv.due_date}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{money(inv.total)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Badge className={`capitalize border-0 ${statusStyle[inv.status]}`}>{inv.status}</Badge>
                      {inv.wave_synced && <Badge variant="outline" className="text-[0.6rem] border-primary/30 text-primary">Wave</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {isAdmin && waveConnected && !inv.wave_synced && (
                        <button data-testid="sync-wave-btn" disabled={syncing === inv.id} onClick={() => syncWave(inv)} title="Sync to Wave" className="p-2 rounded-md hover:bg-primary/10 transition-colors duration-200 disabled:opacity-50">
                          <Plug className={`h-4 w-4 text-primary ${syncing === inv.id ? "animate-pulse" : ""}`} />
                        </button>
                      )}
                      <button data-testid="edit-invoice-btn" onClick={() => openEdit(inv)} className="p-2 rounded-md hover:bg-muted transition-colors duration-200"><Pencil className="h-4 w-4 text-muted-foreground" /></button>
                      <button data-testid="delete-invoice-btn" onClick={() => remove(inv.id)} className="p-2 rounded-md hover:bg-destructive/10 transition-colors duration-200"><Trash2 className="h-4 w-4 text-destructive" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? `Edit ${form.invoice_number || "Invoice"}` : "New Invoice"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Customer *</Label>
                <Select value={form.customer_id || "none"} onValueChange={(v) => onCustomer(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="invoice-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["draft", "sent", "paid", "overdue"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Issue Date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Line Items</Label>
                <Button type="button" variant="ghost" size="sm" data-testid="add-line-item-btn" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" /> Add item</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-6" data-testid="line-item-desc" placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
                    <Input className="col-span-3" type="number" placeholder="Unit $" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                    <button type="button" onClick={() => removeItem(i)} className="col-span-1 p-2 rounded hover:bg-destructive/10 transition-colors duration-200"><X className="h-4 w-4 text-muted-foreground" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
              <div className="space-y-1.5 w-32">
                <Label>Tax Rate (%)</Label>
                <Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} />
              </div>
              <div className="w-full sm:w-56 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">{money(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="tabular-nums">{money(tax)}</span></div>
                <div className="flex justify-between font-display font-bold text-base pt-1.5 border-t border-border"><span>Total</span><span data-testid="invoice-total" className="tabular-nums">{money(total)}</span></div>
              </div>
            </div>

            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-invoice-btn" onClick={save} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">
              <Check className="h-4 w-4 mr-1.5" /> Save Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
